import { Link, Outlet, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTenantStore } from '../../stores/tenant.store';
import { usePermissions } from '../../context/PermissionsContext';
import { apiService } from '../../services/api.service';
import { breadcrumbsForTreasuryPath } from './treasuryRoutes';
import { formatMoney, formatDate } from './treasuryUi';

/**
 * Shell TX1 del módulo Tesorería.
 * Empresa = tenant; disponible total solo si hay permiso cash:write.
 */
export function TreasuryLayout() {
  const location = useLocation();
  const legalEntityId = useTenantStore((s) => s.legalEntityId);
  const { can } = usePermissions();
  const canSeePosition = can('cash', 'write');

  const [entityLabel, setEntityLabel] = useState<string>('—');
  const [baseCurrency, setBaseCurrency] = useState<string>('ARS');
  const [availableTotal, setAvailableTotal] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!legalEntityId) {
        setEntityLabel('Sin empresa');
        setBaseCurrency('ARS');
        setAvailableTotal(null);
        return;
      }
      try {
        const list = await apiService.listLegalEntities();
        const le = (list ?? []).find((e: any) => e.id === legalEntityId);
        if (cancelled) return;
        setEntityLabel(le ? `${le.code} · ${le.legalName}` : legalEntityId);
        setBaseCurrency(le?.baseCurrency || 'ARS');
      } catch {
        if (!cancelled) setEntityLabel(legalEntityId);
      }

      if (canSeePosition) {
        try {
          const summary = await apiService.getTreasurySummary(legalEntityId);
          if (!cancelled) {
            setAvailableTotal(
              summary?.kpis?.totalAvailable != null ? Number(summary.kpis.totalAvailable) : null,
            );
          }
        } catch {
          if (!cancelled) setAvailableTotal(null);
        }
      } else if (!cancelled) {
        setAvailableTotal(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [legalEntityId, canSeePosition]);

  const crumbs = breadcrumbsForTreasuryPath(location.pathname);
  const today = formatDate(new Date());

  return (
    <div className="p-6 space-y-4 max-w-6xl mx-auto">
      <header className="space-y-3 border-b border-gray-200 pb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tesorería</h1>
            <nav className="mt-1 flex flex-wrap items-center gap-1 text-sm text-gray-500" aria-label="Breadcrumb">
              {crumbs.map((c, i) => (
                <span key={`${c.label}-${i}`} className="flex items-center gap-1">
                  {i > 0 && <span className="text-gray-300">/</span>}
                  {c.to && i < crumbs.length - 1 ? (
                    <Link to={c.to} className="hover:text-gray-800">
                      {c.label}
                    </Link>
                  ) : (
                    <span className={i === crumbs.length - 1 ? 'text-gray-800 font-medium' : ''}>
                      {c.label}
                    </span>
                  )}
                </span>
              ))}
            </nav>
          </div>
          <div className="flex flex-wrap items-end gap-4 text-sm">
            <div>
              <div className="text-xs text-gray-500">Empresa</div>
              <div className="font-medium text-gray-900 max-w-[16rem] truncate" title={entityLabel}>
                {entityLabel}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Fecha</div>
              <div className="font-medium text-gray-900">{today}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Moneda</div>
              <div className="font-medium text-gray-900">{baseCurrency}</div>
            </div>
            {canSeePosition && availableTotal != null && (
              <div>
                <div className="text-xs text-gray-500">Disponible total</div>
                <div className="font-semibold text-gray-900 tabular-nums">
                  {formatMoney(availableTotal, baseCurrency || 'ARS')}
                </div>
              </div>
            )}
          </div>
        </div>
        <p className="text-xs text-gray-500 max-w-3xl">
          Distinguí <strong>Mi caja</strong> (sesión del cajero) de la{' '}
          <strong>posición de tesorería</strong> (Central, Madres y Operativas). En{' '}
          <strong>Bancos</strong> registrás depósitos y extracciones; la conciliación compara el
          extracto contra esos movimientos.
        </p>
      </header>
      <Outlet />
    </div>
  );
}
